import { useMemo } from "react";
import {
  calculateEstimatedTime,
  calculateGroupCampaignTime
} from "../../utils/campaignCalculations";

/**
 * Hook para cálculos de campañas (contactos y grupos)
 * Fecha: 2026-01-08
 * Descripción: Hook reutilizable para calcular tiempos estimados y totales
 * 
 * @param {string} type - Tipo de campaña: 'contacts' | 'groups'
 * @param {number} totalItems - Total de items (contactos o grupos)
 * @param {number} tiempoInicial - Tiempo inicial entre mensajes (segundos)
 * @param {number} cadaXItems - Después de cuántos items cambia el intervalo
 * @param {number} nuevoTiempo - Nuevo tiempo de envío (segundos)
 * @returns {Object} - Objeto con cálculos de tiempo estimado
 */
const useCampaignCalculations = (
  type = 'contacts',
  totalItems = 0,
  tiempoInicial = 45,
  cadaXItems = 20,
  nuevoTiempo = 60
) => {
  const calculatedTime = useMemo(() => {
    if (!totalItems || totalItems === 0) {
      return {
        segundos: 0,
        minutos: 0,
        horas: 0,
        minutosRestantes: 0,
        segundosRestantes: 0,
        texto: "0 segundos"
      };
    }

    // Usar función específica según el tipo
    if (type === 'groups') {
      return calculateGroupCampaignTime(
        totalItems,
        tiempoInicial,
        cadaXItems,
        nuevoTiempo
      );
    } else {
      // Por defecto, usar función para contactos
      const result = calculateEstimatedTime(
        totalItems,
        tiempoInicial,
        cadaXItems,
        nuevoTiempo
      );
      // Asegurar que tenga segundosRestantes para consistencia
      return {
        ...result,
        segundosRestantes: result.segundos % 60
      };
    }
  }, [type, totalItems, tiempoInicial, cadaXItems, nuevoTiempo]);

  return {
    calculatedTime,
    totalItems
  };
};

export default useCampaignCalculations;
